import { useState } from 'react';

interface DocumentEntry {
  id: string;
  filename: string;
  uploadedAt: string;
  vaultPath: string;
}

interface LivingRoomDocumentUploadModalProps {
  titleId: string;
  menteeToken?: string;
  onClose: () => void;
}

const formatMenteeVaultFolder = (menteeToken?: string) => {
  if (!menteeToken) return '[PRO_XXXX]';
  return `[${menteeToken.replace(/-/g, '_')}]`;
};

const buildVaultPath = (menteeToken: string | undefined, filename: string) => {
  const folder = formatMenteeVaultFolder(menteeToken);
  return `Mentor Project Vault / Private Mentee Folder / ${folder} / business-industry-general-documents / ${filename}`;
};

export default function LivingRoomDocumentUploadModal({
  titleId,
  menteeToken,
  onClose,
}: LivingRoomDocumentUploadModalProps) {
  const [documents, setDocuments] = useState<DocumentEntry[]>([]);
  const [dragActive, setDragActive] = useState(false);

  const addDocuments = (files: FileList | null) => {
    if (!files?.length) return;
    const uploadedAt = new Date().toLocaleString();
    const newEntries: DocumentEntry[] = Array.from(files).map((file) => ({
      id: `doc-${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      filename: file.name,
      uploadedAt,
      vaultPath: buildVaultPath(menteeToken, file.name),
    }));
    setDocuments((prev) => [...prev, ...newEntries]);
  };

  return (
    <div
      className="living-room-doc-upload-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={onClose}
    >
      <div
        className="living-room-doc-upload-modal"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id={titleId} className="section-header living-room-doc-upload-title">
          Upload Business/Industry &amp; General Documents
        </h2>
        <p className="living-room-doc-upload-intro">
          Add business plans, industry briefs, and general reference files for your mentoring sessions.
        </p>

        <div
          className={`living-room-doc-upload-dropzone ${dragActive ? 'living-room-doc-upload-dropzone-active' : ''}`}
          onDragOver={(event) => {
            event.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragActive(false);
            addDocuments(event.dataTransfer.files);
          }}
        >
          <p className="living-room-doc-upload-label">Drop files/documents here.</p>
          <label className="btn-primary living-room-doc-upload-btn">
            Select General Documents
            <input
              type="file"
              multiple
              className="living-room-doc-upload-input"
              onChange={(event) => addDocuments(event.target.files)}
            />
          </label>
        </div>

        {documents.length > 0 && (
          <div className="living-room-doc-upload-list-wrap">
            <p className="living-room-doc-upload-list-heading">Uploaded files</p>
            <ul className="living-room-doc-upload-list">
              {documents.map((document) => (
                <li key={document.id} className="living-room-doc-upload-list-item">
                  <span className="living-room-doc-upload-filename">{document.filename}</span>
                  <span className="living-room-doc-upload-timestamp">{document.uploadedAt}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <button type="button" className="btn-primary living-room-doc-upload-close" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}
